/**
 * Pure snap-to-align math for the e-sign template editor.
 *
 * While a field is dragged (or placed), its edges and centers are compared
 * against other fields' edges/centers, the page edges, and the page's
 * horizontal center. When an alignment is within the threshold, the dragged
 * position snaps to it and a guide line describes where to draw the visual
 * indicator. All coordinates are in the editor's stacked-canvas space (the
 * same space fields are positioned in).
 */

export interface SnapRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface SnapGuide {
    orientation: 'vertical' | 'horizontal';
    /** x for vertical guides, y for horizontal guides. */
    position: number;
    /** Span of the guide along its axis (y-range for vertical, x-range for horizontal). */
    start: number;
    end: number;
}

export interface SnapResult {
    x: number;
    y: number;
    snappedX: boolean;
    snappedY: boolean;
    guides: SnapGuide[];
}

export const SNAP_THRESHOLD = 6;

interface AxisTarget {
    position: number;
    /** Rect the target came from, for guide span; null for page-level targets. */
    source: SnapRect | null;
}

interface AxisSnap {
    delta: number;
    target: AxisTarget;
}

function bestAxisSnap(edges: number[], targets: AxisTarget[], threshold: number): AxisSnap | null {
    let best: AxisSnap | null = null;

    for (const edge of edges) {
        for (const target of targets) {
            const delta = target.position - edge;
            if (Math.abs(delta) <= threshold && (best === null || Math.abs(delta) < Math.abs(best.delta))) {
                best = {delta, target};
            }
        }
    }

    return best;
}

function rectTargetsX(rect: SnapRect): AxisTarget[] {
    return [
        {position: rect.x, source: rect},
        {position: rect.x + rect.w / 2, source: rect},
        {position: rect.x + rect.w, source: rect},
    ];
}

function rectTargetsY(rect: SnapRect): AxisTarget[] {
    return [
        {position: rect.y, source: rect},
        {position: rect.y + rect.h / 2, source: rect},
        {position: rect.y + rect.h, source: rect},
    ];
}

export function snapRect(
    moving: SnapRect,
    options: {
        /** Rects whose vertical edges/centers are x-snap targets (any page). */
        xTargetRects: SnapRect[];
        /** Rects whose horizontal edges/centers are y-snap targets (same page). */
        yTargetRects: SnapRect[];
        pageWidth: number;
        threshold?: number;
    },
): SnapResult {
    const threshold = options.threshold ?? SNAP_THRESHOLD;

    const xTargets: AxisTarget[] = [
        {position: 0, source: null},
        {position: options.pageWidth / 2, source: null},
        {position: options.pageWidth, source: null},
        ...options.xTargetRects.flatMap(rectTargetsX),
    ];
    const yTargets: AxisTarget[] = options.yTargetRects.flatMap(rectTargetsY);

    const movingXEdges = [moving.x, moving.x + moving.w / 2, moving.x + moving.w];
    const movingYEdges = [moving.y, moving.y + moving.h / 2, moving.y + moving.h];

    const snapX = bestAxisSnap(movingXEdges, xTargets, threshold);
    const snapY = bestAxisSnap(movingYEdges, yTargets, threshold);

    const x = snapX ? moving.x + snapX.delta : moving.x;
    const y = snapY ? moving.y + snapY.delta : moving.y;

    const guides: SnapGuide[] = [];
    const guidePadding = 24;

    if (snapX) {
        const source = snapX.target.source;
        guides.push({
            orientation: 'vertical',
            position: snapX.target.position,
            start: Math.min(y, source?.y ?? y) - guidePadding,
            end: Math.max(y + moving.h, source ? source.y + source.h : y + moving.h) + guidePadding,
        });
    }

    if (snapY) {
        const source = snapY.target.source;
        guides.push({
            orientation: 'horizontal',
            position: snapY.target.position,
            start: Math.min(x, source?.x ?? x) - guidePadding,
            end: Math.max(x + moving.w, source ? source.x + source.w : x + moving.w) + guidePadding,
        });
    }

    return {
        x,
        y,
        snappedX: snapX !== null,
        snappedY: snapY !== null,
        guides,
    };
}
